import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Grid,
  Card,
  CardContent,
  alpha,
  Chip
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  DirectionsCar as CarIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as ClockIcon
} from '@mui/icons-material';
import { DataTable } from '../components';
import { useAsync } from '../hooks';
import apiService from '../services/api';
import { formatDate } from '../utils/helpers';

/**
 * PickupLogsPage component for displaying pickup logs with SafeLine theme
 */
const PickupLogsPage = () => {
  // SafeLine Pickup Color Scheme
  const safeLineColors = {
    flameOrange: '#ff6b35',
    brightOrange: '#ff9500', 
    deepOrange: '#e55100',
    orangeTint: '#fff4f0',
    pureWhite: '#ffffff',
    lightGray: '#f8f9fa',
    coolGray: '#e9ecef',
    mediumGray: '#dee2e6',
    emerald: '#10b981',
    lightEmerald: '#d1fae5',
    forestGreen: '#047857',
    amber: '#f59e0b',
    lightAmber: '#fef3c7',
    darkAmber: '#d97706',
    red: '#ef4444',
    lightRed: '#fee2e2',
    darkRed: '#dc2626',
    darkCharcoal: '#1f2937',
    mediumCharcoal: '#374151',
    lightCharcoal: '#6b7280',
    mutedGray: '#9ca3af'
  };

  // Set up async state for fetching pickup logs
  const {
    execute: fetchPickupLogs,
    value: pickupLogs,
    loading,
    error,
  } = useAsync(apiService.pickupLogs.getAll, true);

  // Calculate statistics
  const totalPickups = pickupLogs?.length || 0;
  const completedPickups = pickupLogs?.filter((log) => log.status === 'COMPLETED').length || 0;
  const pendingPickups = pickupLogs?.filter((log) => log.status === 'PENDING').length || 0;
  const completionRate = totalPickups > 0 ? Math.round((completedPickups / totalPickups) * 100) : 0;

  // Define table columns with SafeLine styling
  const columns = [
    {
      field: 'student_name',
      headerName: 'Student',
      minWidth: 150,
    },
    {
      field: 'grade',
      headerName: 'Grade',
      width: 100,
      align: 'center',
    },
    {
      field: 'guardian_name',
      headerName: 'Guardian',
      minWidth: 150,
    },
    {
      field: 'license_plate_id',
      headerName: 'License Plate',
      minWidth: 150,
      valueGetter: (row) => row.license_plate_id.split('_').pop(),
    },
    {
      field: 'timestamp',
      headerName: 'Time',
      minWidth: 180,
      renderCell: (value) => formatDate(value),
    },
    {
      field: 'method',
      headerName: 'Method',
      minWidth: 150,
      renderCell: (value) => (
        <Typography variant="body2" sx={{ 
          textTransform: 'capitalize',
          color: safeLineColors.mediumCharcoal,
          fontWeight: 'medium'
        }}>
          {value.replace('_', ' ')}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 120,
      renderCell: (value) => {
        const getCustomStatusColor = (status) => {
          switch (status.toLowerCase()) {
            case 'completed':
              return {
                backgroundColor: safeLineColors.lightEmerald,
                color: safeLineColors.forestGreen,
                borderColor: safeLineColors.emerald
              };
            case 'pending':
              return {
                backgroundColor: safeLineColors.lightAmber,
                color: safeLineColors.darkAmber,
                borderColor: safeLineColors.amber
              };
            case 'failed':
              return {
                backgroundColor: safeLineColors.lightRed,
                color: safeLineColors.darkRed,
                borderColor: safeLineColors.red
              };
            default:
              return {
                backgroundColor: safeLineColors.mediumGray,
                color: safeLineColors.darkCharcoal,
                borderColor: safeLineColors.coolGray
              };
          }
        };

        const getStatusIcon = (status) => {
          switch (status.toLowerCase()) {
            case 'completed':
              return <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />;
            case 'pending':
              return <ClockIcon sx={{ fontSize: 16, mr: 0.5 }} />;
            case 'failed':
              return <ErrorIcon sx={{ fontSize: 16, mr: 0.5 }} />;
            default:
              return <WarningIcon sx={{ fontSize: 16, mr: 0.5 }} />;
          }
        };

        const statusStyle = getCustomStatusColor(value);

        return (
          <Chip 
            icon={getStatusIcon(value)}
            label={value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()} 
            size="small"
            sx={{
              ...statusStyle,
              border: `1px solid ${statusStyle.borderColor}`,
              fontWeight: 'medium',
              '& .MuiChip-icon': {
                color: statusStyle.color
              }
            }}
          />
        );
      },
    },
  ];
  
  return (
    <Box sx={{ backgroundColor: safeLineColors.lightGray, minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ 
        borderTop: `4px solid ${safeLineColors.flameOrange}`,
        backgroundColor: safeLineColors.pureWhite,
        borderRadius: '8px 8px 0 0',
        border: `1px solid ${safeLineColors.coolGray}`,
        p: 3,
        mb: 3,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center'
      }}>
        <Box>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 600,
              color: safeLineColors.darkCharcoal,
              mb: 1
            }}
          >
            Pickup Logs
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: safeLineColors.mediumCharcoal
            }}
          >
            Monitor and analyze student pickup activities
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => fetchPickupLogs()}
          disabled={loading}
          sx={{
            background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
            boxShadow: `0 4px 10px ${alpha(safeLineColors.flameOrange, 0.3)}`,
            fontWeight: 'bold',
            borderRadius: 2,
            textTransform: 'none',
            '&:hover': {
              background: `linear-gradient(90deg, ${safeLineColors.deepOrange}, ${safeLineColors.flameOrange})`,
              boxShadow: `0 6px 15px ${alpha(safeLineColors.flameOrange, 0.4)}`,
              transform: 'translateY(-1px)'
            },
            '&:disabled': {
              background: safeLineColors.mutedGray,
              color: safeLineColors.pureWhite
            }
          }}
        >
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </Box>
      
      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={3}
            sx={{
              background: `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
              color: safeLineColors.pureWhite,
              borderRadius: 3,
              overflow: 'hidden',
              position: 'relative',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 25px ${alpha(safeLineColors.flameOrange, 0.3)}`
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100px',
                height: '100px',
                background: `radial-gradient(circle, ${alpha(safeLineColors.pureWhite, 0.1)}, transparent)`,
                borderRadius: '50%',
                transform: 'translate(30px, -30px)'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <CarIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {totalPickups}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'medium', opacity: 0.95 }}>
                Total Pickups
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                All-time pickup records
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={3}
            sx={{
              background: `linear-gradient(135deg, ${safeLineColors.emerald}, ${safeLineColors.forestGreen})`,
              color: safeLineColors.pureWhite,
              borderRadius: 3,
              overflow: 'hidden',
              position: 'relative',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 25px ${alpha(safeLineColors.emerald, 0.3)}`
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100px',
                height: '100px',
                background: `radial-gradient(circle, ${alpha(safeLineColors.pureWhite, 0.1)}, transparent)`,
                borderRadius: '50%',
                transform: 'translate(30px, -30px)'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {completedPickups}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'medium', opacity: 0.95 }}>
                Completed
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                Successfully processed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={3}
            sx={{
              background: `linear-gradient(135deg, ${safeLineColors.amber}, ${safeLineColors.darkAmber})`,
              color: safeLineColors.pureWhite,
              borderRadius: 3,
              overflow: 'hidden',
              position: 'relative',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 25px ${alpha(safeLineColors.amber, 0.3)}`
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100px',
                height: '100px',
                background: `radial-gradient(circle, ${alpha(safeLineColors.pureWhite, 0.1)}, transparent)`,
                borderRadius: '50%',
                transform: 'translate(30px, -30px)'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <ScheduleIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {pendingPickups}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'medium', opacity: 0.95 }}>
                Pending
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                Awaiting completion
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={3}
            sx={{
              background: `linear-gradient(135deg, ${safeLineColors.emerald}, ${safeLineColors.forestGreen})`,
              color: safeLineColors.pureWhite,
              borderRadius: 3,
              overflow: 'hidden',
              position: 'relative',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 25px ${alpha(safeLineColors.emerald, 0.3)}`
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100px',
                height: '100px',
                background: `radial-gradient(circle, ${alpha(safeLineColors.pureWhite, 0.1)}, transparent)`,
                borderRadius: '50%',
                transform: 'translate(30px, -30px)'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <TrendingUpIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {completionRate}%
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'medium', opacity: 0.95 }}>
                Success Rate
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                Completion percentage
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Data Table */}
      <Card
        elevation={3}
        sx={{
          borderRadius: 2,
          backgroundColor: safeLineColors.pureWhite,
          border: `1px solid ${safeLineColors.coolGray}`,
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
          }
        }}
      >
        <Box sx={{ 
          p: 3, 
          backgroundColor: safeLineColors.orangeTint,
          borderBottom: `1px solid ${safeLineColors.coolGray}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2,
                backgroundColor: safeLineColors.flameOrange,
                mr: 2
              }}>
                <PeopleIcon sx={{ color: safeLineColors.pureWhite, fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold', 
                  color: safeLineColors.darkCharcoal 
                }}>
                  Recent Pickup Logs
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: safeLineColors.mediumCharcoal 
                }}>
                  Detailed pickup activity records
                </Typography>
              </Box>
            </Box>
            <Chip 
              label={`${totalPickups} total records`}
              sx={{
                backgroundColor: safeLineColors.flameOrange,
                color: safeLineColors.pureWhite,
                fontWeight: 'medium'
              }}
            />
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <DataTable
            data={pickupLogs || []}
            columns={columns}
            loading={loading}
            error={error?.message}
            searchFields={['student_name', 'guardian_name', 'license_plate_id']}
            initialSort="timestamp"
            initialSortDirection="desc"
            onRowClick={(row) => console.log('Row clicked:', row)}
            showRefresh
            onRefresh={() => fetchPickupLogs()}
            sx={{
              '& .MuiDataGrid-root': {
                border: 'none',
                backgroundColor: safeLineColors.pureWhite
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: safeLineColors.lightGray,
                borderBottom: `2px solid ${safeLineColors.coolGray}`,
                '& .MuiDataGrid-columnHeader': {
                  color: safeLineColors.darkCharcoal,
                  fontWeight: 'bold'
                }
              },
              '& .MuiDataGrid-cell': {
                borderBottom: `1px solid ${safeLineColors.coolGray}`,
                color: safeLineColors.mediumCharcoal
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: safeLineColors.orangeTint
              }
            }}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default PickupLogsPage;